import classnames from 'classnames'
import * as React from 'react'

export interface SelectableItem<Id extends string = string> {
  /** Display text on option in select */
  label: string
  /** Unique id of option */
  id: Id
  /** optional: determines if option is selectable */
  disabled?: boolean
  /** optional: determines if option should be hidden */
  hidden?: boolean
}

export interface Props<Id extends string = string> {
  id?: string
  /** Options to populate select T extends SelectableItem */
  items: readonly SelectableItem<Id>[]
  /** Callback to update selected option id */
  onSelect: (id: SelectableItem<Id>['id']) => void
  /** String ID of the selected item from `items`;
   * this must be provided to set the selection,
   * the Select component does not maintain state.
   */

  selected?: SelectableItem<Id>['id']
  /** Provides a `aria-label` for the select menu. */
  menuLabel?: string
  /** Disables the select menu entirely. */
  disabled?: boolean
  /** Additional display classes to be applied to the `select` element. */
  displayClasses?: string
  /** A string which, if provided, creates an unselectable default state the the element */
  defaultItemText?: string
  /** Optional identifier for the AQA */
  dataSeleniumTarget?: string
}

const DEFAULT_ITEM_VALUE = ''

/**

 * @visibleName Select
 */
function Select<Id extends string = string>({
  id,
  items,
  selected,
  onSelect,
  menuLabel,
  disabled,
  displayClasses,
  defaultItemText,
  dataSeleniumTarget,
}: Props<Id>): JSX.Element {
  const selectableItems = React.useMemo<
    Readonly<SelectableItem<Id | typeof DEFAULT_ITEM_VALUE>[]>
  >(() => {
    if (defaultItemText !== undefined) {
      return [
        {label: defaultItemText, id: DEFAULT_ITEM_VALUE, disabled: true},
        ...items,
      ]
    }
    return items
  }, [defaultItemText, items])

  const selectable = selectableItems.map((item, index) => {
    return (
      !item.hidden && (
        <option
          key={item.id}
          disabled={item.disabled}
          value={item.id}
          data-selenium-target={`${menuLabel || 'select-child'}(${index})`}
        >
          {item.label}
        </option>
      )
    )
  })

  const selectedValue = React.useMemo(() => {
    if (selected !== undefined) {
      return selected
    }
    return defaultItemText !== undefined ? DEFAULT_ITEM_VALUE : undefined
  }, [defaultItemText, selected])

  const classes = classnames('select--select', displayClasses)

  return (
    <div className="select">
      <select
        id={id}
        className={classes}
        aria-label={menuLabel || 'Select Menu'}
        onChange={(ev) => onSelect(ev.target.value as Id)}
        value={selectedValue}
        disabled={disabled}
        data-selenium-target={dataSeleniumTarget}
        data-testid={dataSeleniumTarget}
      >
        {selectable}
      </select>
    </div>
  )
}

export default Select
